import { useState, useRef, useEffect, useCallback } from "react";
import { UploadCloud, Camera, X, CheckCircle2, AlertTriangle, Smile, RefreshCw } from "lucide-react";
import { detectFace } from "../../utils/faceDetection";
import styles from "./FileUpload.module.css";

const FileUpload = ({
  value,
  onChange,
  accept = "image/*",
  maxSizeMB = 5,
  enableLiveCamera = false,
  liveOnly = false,
  requireFaceDetection = false,
  helperText = "PNG, JPG up to 5MB",
  disabled = false,
  className = "",
}) => {
  const [preview, setPreview] = useState(value || "");
  const [isDragActive, setIsDragActive] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [camStream, setCamStream] = useState(null);
  const [facingMode, setFacingMode] = useState("user");

  // Face Detection State
  const [faceStatus, setFaceStatus] = useState("searching"); // 'searching' | 'detected' | 'warning'
  const [hasVerifiedFace, setHasVerifiedFace] = useState(false);
  const [faceWarningMessage, setFaceWarningMessage] = useState("");

  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const detectIntervalRef = useRef(null);

  useEffect(() => {
    if (value && typeof value === "string") {
      setPreview(value);
      if (requireFaceDetection && value) {
        setHasVerifiedFace(true);
      }
    } else if (!value) {
      setPreview("");
      setHasVerifiedFace(false);
    }
  }, [value, requireFaceDetection]);

  // Clean up camera stream and face detection interval on unmount
  useEffect(() => {
    return () => {
      if (detectIntervalRef.current) {
        clearInterval(detectIntervalRef.current);
      }
      if (camStream) {
        camStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [camStream]);

  const handleFileChange = async (file) => {
    if (!file || liveOnly) return;

    if (file.size > maxSizeMB * 1024 * 1024) {
      alert(`File size exceeds limit of ${maxSizeMB}MB.`);
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const dataUrl = e.target.result;
      setPreview(dataUrl);

      let isFaceDetected = true;
      if (requireFaceDetection) {
        try {
          const res = await detectFace(dataUrl);
          isFaceDetected = res.hasFace;
          if (isFaceDetected) {
            setHasVerifiedFace(true);
            setFaceWarningMessage("");
          } else {
            setHasVerifiedFace(false);
            setFaceWarningMessage(
              "No clear face detected in uploaded photo. A front-facing photo is recommended for security clearance."
            );
          }
        } catch (err) {
          console.warn("Face detection check failed:", err);
        }
      }

      if (onChange) {
        onChange(file, dataUrl, { hasFace: isFaceDetected });
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragActive(false);
    if (disabled || liveOnly) return;
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    setPreview("");
    setHasVerifiedFace(false);
    setFaceWarningMessage("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (onChange) onChange(null, "", { hasFace: false });
  };

  // Live face detection loop while camera is active
  const startLiveFaceDetection = useCallback(() => {
    if (!requireFaceDetection) return;

    if (detectIntervalRef.current) {
      clearInterval(detectIntervalRef.current);
    }

    detectIntervalRef.current = setInterval(async () => {
      if (!videoRef.current || videoRef.current.readyState < 2) return;
      try {
        const result = await detectFace(videoRef.current);
        if (result.hasFace) {
          setFaceStatus("detected");
        } else {
          setFaceStatus("searching");
        }
      } catch (err) {
        // Silent fail during live polling
      }
    }, 350);
  }, [requireFaceDetection]);

  // Camera capture handlers
  const openCamera = async (mode = facingMode) => {
    try {
      setFaceStatus("searching");
      setFaceWarningMessage("");
      if (camStream) {
        camStream.getTracks().forEach((track) => track.stop());
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode, width: { ideal: 640 }, height: { ideal: 480 } },
      });
      setCamStream(stream);
      setIsCameraOpen(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          startLiveFaceDetection();
        }
      }, 150);
    } catch (err) {
      alert("Unable to access camera: " + err.message);
    }
  };

  const toggleCamera = () => {
    const nextMode = facingMode === "user" ? "environment" : "user";
    setFacingMode(nextMode);
    openCamera(nextMode);
  };

  const closeCamera = () => {
    if (detectIntervalRef.current) {
      clearInterval(detectIntervalRef.current);
      detectIntervalRef.current = null;
    }
    if (camStream) {
      camStream.getTracks().forEach((track) => track.stop());
      setCamStream(null);
    }
    setIsCameraOpen(false);
  };

  const capturePhoto = async () => {
    if (!videoRef.current) return;
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth || 640;
    canvas.height = videoRef.current.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.88);

    let isFaceDetected = true;
    if (requireFaceDetection) {
      const check = await detectFace(canvas);
      isFaceDetected = check.hasFace;
      if (!isFaceDetected) {
        const proceed = window.confirm(
          "⚠️ Face not clearly detected within the guide circle.\n\nWould you like to use this photo anyway, or retake for faster gate approval?"
        );
        if (!proceed) {
          setFaceStatus("warning");
          return;
        }
      }
    }

    setPreview(dataUrl);
    setHasVerifiedFace(isFaceDetected);
    setFaceWarningMessage(
      isFaceDetected
        ? ""
        : "Photo captured without verified face. Guard may verify ID manually."
    );

    if (onChange) {
      onChange(null, dataUrl, { hasFace: isFaceDetected });
    }
    closeCamera();
  };

  return (
    <div className={`${styles.container} ${className}`.trim()}>
      {!liveOnly && (
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          style={{ display: "none" }}
          disabled={disabled}
          onChange={(e) => {
            if (e.target.files && e.target.files[0]) {
              handleFileChange(e.target.files[0]);
            }
          }}
        />
      )}

      {preview ? (
        <div>
          <div className={styles.previewCard}>
            <img src={preview} alt="Security Photo" className={styles.previewThumb} />
            <div className={styles.previewInfo}>
              <div className={styles.fileName}>Security Photo Attached</div>
              {hasVerifiedFace ? (
                <div className={styles.faceVerifiedBadge}>
                  <CheckCircle2 size={13} />
                  <span>Face Verified</span>
                </div>
              ) : (
                <div className={styles.subText}>Ready for gate check-in</div>
              )}
            </div>
            {!disabled && (
              <button
                type="button"
                className={styles.removeBtn}
                onClick={handleRemove}
                aria-label="Remove attachment"
              >
                <X size={16} />
              </button>
            )}
          </div>
          {faceWarningMessage && (
            <div className={styles.faceWarningBanner}>
              <AlertTriangle size={15} />
              <span>{faceWarningMessage}</span>
            </div>
          )}
        </div>
      ) : (
        <div
          className={`${styles.dropzone} ${liveOnly ? styles.liveOnlyDropzone : ""} ${
            isDragActive ? styles.isDragActive : ""
          } ${disabled ? styles.disabled : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled && !liveOnly) setIsDragActive(true);
          }}
          onDragLeave={() => setIsDragActive(false)}
          onDrop={handleDrop}
          onClick={() => {
            if (disabled) return;
            if (liveOnly) {
              openCamera();
            } else if (fileInputRef.current) {
              fileInputRef.current.click();
            }
          }}
        >
          <div className={styles.iconCircle}>
            {liveOnly ? <Camera size={22} /> : <UploadCloud size={22} />}
          </div>
          <div className={styles.mainText}>
            {liveOnly ? (
              <strong>Tap to open live camera</strong>
            ) : (
              <>
                <strong>Click to upload photo</strong> or drag and drop
              </>
            )}
          </div>
          <div className={styles.subText}>
            {liveOnly
              ? "Live camera selfie required for security verification"
              : requireFaceDetection
              ? "Front-facing selfie required (Clear face, good lighting)"
              : helperText}
          </div>
        </div>
      )}

      {enableLiveCamera && !liveOnly && !preview && !disabled && (
        <button
          type="button"
          className={styles.cameraActionBtn}
          onClick={() => openCamera()}
        >
          <Camera size={16} />
          <span>Take Live Photo (Webcam / Phone Cam)</span>
        </button>
      )}

      {isCameraOpen && (
        <div className={styles.camModalBackdrop}>
          <div className={styles.camModalBox}>
            <div className={styles.camModalHeader}>
              <h4 className={styles.camModalTitle}>Live Camera Capture</h4>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <button
                  type="button"
                  className={styles.flipBtn}
                  onClick={toggleCamera}
                  title="Switch camera"
                >
                  <RefreshCw size={14} />
                  <span>Flip</span>
                </button>
                <button
                  type="button"
                  className={styles.removeBtn}
                  onClick={closeCamera}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className={styles.camVideoContainer}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className={styles.camVideo}
              />

              {/* Biometric Face Guide Oval */}
              {requireFaceDetection && (
                <div className={styles.biometricGuideOverlay}>
                  <div
                    className={`${styles.biometricOval} ${
                      faceStatus === "detected"
                        ? styles.detected
                        : faceStatus === "warning"
                        ? styles.warning
                        : styles.searching
                    }`}
                  />
                  <div
                    className={`${styles.detectionStatusBadge} ${
                      faceStatus === "detected" ? styles.detected : styles.searching
                    }`}
                  >
                    {faceStatus === "detected" ? (
                      <>
                        <CheckCircle2 size={14} />
                        <span>Face Detected • Ready to Snap</span>
                      </>
                    ) : (
                      <>
                        <Smile size={14} />
                        <span>Center Face in Circle</span>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className={styles.camFooter}>
              <button
                type="button"
                className={styles.cameraActionBtn}
                style={{ width: "auto" }}
                onClick={closeCamera}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.cameraActionBtn}
                style={{
                  width: "auto",
                  backgroundColor:
                    faceStatus === "detected" ? "#10b981" : "var(--color-dark-bg)",
                  color: "#ffffff",
                  borderColor:
                    faceStatus === "detected" ? "#10b981" : "var(--color-dark-bg)",
                  fontWeight: 600,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                }}
                onClick={capturePhoto}
              >
                <Camera size={16} />
                <span>Snap Photo</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
